import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, FileText, CalendarDays, Briefcase, MessageSquare, Settings as SettingsIcon,
  LogOut, Search, Download, Printer, X, Loader2, ExternalLink, RefreshCw, Menu,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, API, apiErr } from "../../lib/api";
import { LOGO_URL } from "../../lib/constants";

const token = () => localStorage.getItem("ubm_token");
const fileUrl = (id) => `${API}/files/${id}/download?auth=${token()}`;
const exportUrl = (kind) => `${API}/admin/export/${kind}?auth=${token()}`;

const APP_STATUSES = ["New", "Reviewing", "Interviewing", "Hired", "Declined"];
const BOOKING_STATUSES = ["pending_payment", "confirmed", "completed", "canceled"];
const LEAD_STATUSES = ["new", "contacted", "quoted", "won", "lost"];

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "applications", label: "Applications", icon: FileText },
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "leads", label: "Commercial Leads", icon: Briefcase },
  { id: "contacts", label: "Messages", icon: MessageSquare },
  { id: "settings", label: "Website Management", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [sidebar, setSidebar] = useState(false);

  const doLogout = () => { logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#f4f5f7] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-64 grain-dark text-gray-300 flex flex-col transition-transform ${sidebar ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex items-center gap-3 px-5 h-16 border-b border-charcoal-700">
          <img src={LOGO_URL} alt="UBM" className="h-9 w-9 object-contain" />
          <span className="text-white font-display font-bold">UBM Admin</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => { setTab(n.id); setSidebar(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${tab === n.id ? "bg-primary text-white" : "hover:bg-charcoal-800 text-gray-300"}`}
              data-testid={`admin-nav-${n.id}`}>
              <n.icon size={18} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-charcoal-700">
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium hover:bg-charcoal-800 transition-colors" data-testid="admin-logout">
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </aside>
      {sidebar && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebar(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setSidebar(true)}><Menu /></button>
            <h1 className="font-display font-semibold text-charcoal-900 capitalize">{NAV.find((n) => n.id === tab)?.label}</h1>
          </div>
          <span className="text-sm text-charcoal-500">{user?.email}</span>
        </header>
        <div className="p-5 sm:p-8">
          {tab === "overview" && <Overview setTab={setTab} />}
          {tab === "applications" && <Applications />}
          {tab === "bookings" && <Bookings />}
          {tab === "leads" && <Leads />}
          {tab === "contacts" && <Contacts />}
          {tab === "settings" && <SettingsPanel />}
        </div>
      </div>
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`bg-white border border-gray-200 rounded-md ${className}`}>{children}</div>;
}
const btn = "inline-flex items-center gap-1.5 font-semibold rounded-md transition-colors text-sm";
const inp = "border border-gray-300 rounded-md px-3 py-2 focus:border-primary focus:ring-1 focus:ring-primary outline-none text-sm";

/* ---------------- Overview ---------------- */
function Overview({ setTab }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/summary").then((r) => setS(r.data)).catch(() => {}); }, []);
  const stats = [
    { label: "Total Applications", value: s?.applications, sub: `${s?.new_applications ?? 0} new`, tab: "applications" },
    { label: "Active Bookings", value: s?.bookings, sub: "upcoming & completed", tab: "bookings" },
    { label: "Commercial Leads", value: s?.leads, sub: `${s?.new_leads ?? 0} new`, tab: "leads" },
    { label: "Messages", value: s?.contacts, sub: "contact form", tab: "contacts" },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-overview">
      {stats.map((st) => (
        <button key={st.label} onClick={() => setTab(st.tab)} className="text-left">
          <Card className="p-6 hover:border-primary transition-colors">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{st.label}</div>
            <div className="text-4xl font-bold font-display text-charcoal-900 mt-2">{st.value ?? "—"}</div>
            <div className="text-xs text-primary mt-1">{st.sub}</div>
          </Card>
        </button>
      ))}
    </div>
  );
}

/* ---------------- Applications ---------------- */
function Applications() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/applications", { params: { q: q || undefined, status: status || undefined } })
      .then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-applications">
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
          <input className={`${inp} w-full pl-9`} placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="app-search" />
        </div>
        <select className={inp} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="app-status-filter">
          <option value="">All statuses</option>{APP_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <a href={exportUrl("applications")} className={`${btn} border-2 border-charcoal-900 text-charcoal-900 hover:bg-charcoal-900 hover:text-white px-4 py-2`}><Download size={15} /> CSV</a>
        <button onClick={load} className={`${btn} bg-gray-100 hover:bg-gray-200 text-charcoal-800 px-3 py-2`}><RefreshCw size={15} /></button>
      </div>
      <Card>
        {loading ? <Loading /> : list.length === 0 ? <Empty text="No applications yet." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                <tr><th className="px-4 py-3">Applicant</th><th className="px-4 py-3">Position</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Files</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50" data-testid={`app-row-${a.id}`}>
                    <td className="px-4 py-3"><div className="font-medium text-charcoal-900">{a.applicant_name}</div><div className="text-xs text-gray-500">{a.applicant_email}</div></td>
                    <td className="px-4 py-3">{a.position || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-3 text-gray-500">{a.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3">{a.attachment_count}</td>
                    <td className="px-4 py-3 text-right"><button onClick={() => setDetail(a.id)} className="text-primary font-semibold hover:underline" data-testid={`app-open-${a.id}`}>Open</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {detail && <ApplicationDetail id={detail} onClose={() => setDetail(null)} onChange={load} />}
    </div>
  );
}

function ApplicationDetail({ id, onClose, onChange }) {
  const [app, setApp] = useState(null);
  const [notes, setNotes] = useState("");
  useEffect(() => { api.get(`/admin/applications/${id}`).then((r) => { setApp(r.data); setNotes(r.data.notes || ""); }); }, [id]);

  const setStatus = async (status) => {
    try { await api.patch(`/admin/applications/${id}`, { status }); setApp((a) => ({ ...a, status })); onChange(); toast.success("Status updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveNotes = async () => {
    try { await api.patch(`/admin/applications/${id}`, { notes }); toast.success("Notes saved"); onChange(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const printApp = () => {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Application - ${app.applicant_name}</title>
      <style>body{font-family:Arial;padding:32px;color:#111827} h1{font-size:22px} h2{font-size:15px;border-bottom:2px solid #22b455;padding-bottom:4px;margin-top:24px} table{width:100%;border-collapse:collapse;font-size:13px} td{padding:4px 8px;vertical-align:top;border-bottom:1px solid #eee} td:first-child{font-weight:bold;width:220px;color:#374151}</style>
      </head><body>${renderPrintable(app)}</body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  if (!app) return <Modal onClose={onClose}><Loading /></Modal>;
  const d = app.data || {};
  return (
    <Modal onClose={onClose} wide>
      <div className="flex items-center justify-between mb-4 no-print">
        <div>
          <h2 className="text-xl font-bold text-charcoal-900">{app.applicant_name}</h2>
          <div className="text-sm text-gray-500">{d.position} · applied {app.created_at?.slice(0, 10)}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={printApp} className={`${btn} border-2 border-charcoal-900 text-charcoal-900 hover:bg-charcoal-900 hover:text-white px-3 py-2`} data-testid="app-print"><Printer size={15} /> Print / PDF</button>
          <button onClick={onClose}><X /></button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4 no-print">
        {APP_STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${app.status === s ? "bg-primary text-white border-primary" : "border-gray-300 text-charcoal-700 hover:border-primary"}`} data-testid={`app-set-${s}`}>{s}</button>
        ))}
      </div>

      <div dangerouslySetInnerHTML={{ __html: renderPrintable(app) }} className="prose-sm" />

      {app.attachments?.length > 0 && (
        <div className="mt-6">
          <h3 className="font-semibold text-charcoal-900 mb-2">Attachments</h3>
          <div className="flex flex-wrap gap-2">
            {app.attachments.map((f) => (
              <a key={f.id} href={fileUrl(f.id)} target="_blank" rel="noreferrer" className={`${btn} bg-gray-100 hover:bg-gray-200 text-charcoal-800 px-3 py-2`} data-testid={`att-${f.id}`}>
                <ExternalLink size={14} /> {f.original_filename}
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 no-print">
        <h3 className="font-semibold text-charcoal-900 mb-2">Private Notes</h3>
        <textarea rows={3} className={`${inp} w-full`} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="app-notes" />
        <button onClick={saveNotes} className={`${btn} bg-primary text-white hover:bg-primary-hover px-4 py-2 mt-2`}>Save Notes</button>
      </div>
    </Modal>
  );
}

function renderPrintable(app) {
  const d = app.data || {};
  const row = (k, v) => v ? `<tr><td>${k}</td><td>${String(v)}</td></tr>` : "";
  const simple = Object.entries(d).filter(([, v]) => v && typeof v !== "object");
  const avail = d.availability || {};
  const availRows = Object.entries(avail).map(([day, t]) =>
    `<tr><td>${day}</td><td>${t.off ? "Unavailable" : `${t.start || "—"} to ${t.end || "—"}`}</td></tr>`).join("");
  const emps = (d.employment_history || []).map((e, i) =>
    `<h2>Employer ${i + 1}</h2><table>${row("Company", e.company)}${row("Supervisor", e.supervisor)}${row("Phone", e.phone)}${row("Title", e.title)}${row("Dates", `${e.start || ""} – ${e.end || ""}`)}${row("Pay", `${e.start_pay || ""} → ${e.end_pay || ""}`)}${row("Responsibilities", e.responsibilities)}${row("Reason for Leaving", e.reason)}${row("May Contact", e.contact_ok)}</table>`).join("");
  const rfs = (d.references || []).map((r, i) =>
    `<h2>Reference ${i + 1}</h2><table>${row("Name", r.name)}${row("Relationship", r.relationship)}${row("Company", r.company)}${row("Phone", r.phone)}${row("Email", r.email)}${row("Years Known", r.years_known)}</table>`).join("");
  return `<h1>Employment Application — ${app.applicant_name}</h1>
    <h2>Application Details</h2><table>${simple.map(([k, v]) => row(k.replace(/_/g, " "), v)).join("")}</table>
    <h2>Availability</h2><table>${availRows}</table>
    ${emps}${rfs}
    <h2>Certification</h2><table>${row("Signature", app.signature)}${row("Consent", app.consent ? "Yes" : "No")}${row("Date Signed", d.date_signed)}</table>`;
}

/* ---------------- Bookings ---------------- */
function Bookings() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/bookings").then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = async (id, body) => {
    try { await api.patch(`/admin/bookings/${id}`, body); toast.success("Updated"); load(); setEdit(null); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const refund = async (id) => {
    if (!window.confirm("Issue a refund for this booking?")) return;
    try { const r = await api.post(`/admin/bookings/${id}/refund`, {}); toast.success(r.data.note || "Refund processed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="admin-bookings">
      <div className="flex justify-end mb-4"><a href={exportUrl("bookings")} className={`${btn} border-2 border-charcoal-900 text-charcoal-900 hover:bg-charcoal-900 hover:text-white px-4 py-2`}><Download size={15} /> Export CSV</a></div>
      <Card>
        {loading ? <Loading /> : list.length === 0 ? <Empty text="No bookings yet." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                <tr><th className="px-4 py-3">Date / Time</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Status</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50" data-testid={`booking-row-${b.id}`}>
                    <td className="px-4 py-3"><div className="font-medium text-charcoal-900">{b.date}</div><div className="text-xs text-gray-500">{b.time_window}</div></td>
                    <td className="px-4 py-3"><div>{b.name}</div><div className="text-xs text-gray-500">{b.phone}</div></td>
                    <td className="px-4 py-3">{b.service_name}</td>
                    <td className="px-4 py-3"><div>${Number(b.amount_paid || 0).toFixed(0)} / ${Number(b.total).toFixed(0)}</div><div className="text-xs text-gray-500">{b.payment_status}</div></td>
                    <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setEdit(b)} className="text-primary font-semibold hover:underline mr-3" data-testid={`booking-edit-${b.id}`}>Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {edit && <BookingModal b={edit} onClose={() => setEdit(null)} onSave={update} onRefund={refund} />}
    </div>
  );
}

function BookingModal({ b, onClose, onSave, onRefund }) {
  const [date, setDate] = useState(b.date);
  const [tw, setTw] = useState(b.time_window);
  const [status, setStatus] = useState(b.status);
  return (
    <Modal onClose={onClose}>
      <div className="flex justify-between items-center mb-4"><h2 className="text-lg font-bold text-charcoal-900">Manage Booking</h2><button onClick={onClose}><X /></button></div>
      <div className="text-sm text-charcoal-600 mb-4">{b.name} · {b.service_name} · {b.address}</div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-sm font-medium">Date</label><input type="date" className={`${inp} w-full`} value={date} onChange={(e) => setDate(e.target.value)} data-testid="edit-date" /></div>
        <div><label className="text-sm font-medium">Time Window</label><input className={`${inp} w-full`} value={tw} onChange={(e) => setTw(e.target.value)} data-testid="edit-time" /></div>
      </div>
      <div className="mt-3"><label className="text-sm font-medium">Status</label>
        <select className={`${inp} w-full`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="edit-status">{BOOKING_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
      </div>
      <div className="flex gap-2 mt-5">
        <button onClick={() => onSave(b.id, { date, time_window: tw, status })} className={`${btn} bg-primary text-white hover:bg-primary-hover px-4 py-2 flex-1 justify-center`} data-testid="booking-save">Save Changes</button>
        <button onClick={() => onRefund(b.id)} className={`${btn} border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white px-4 py-2`} data-testid="booking-refund">Refund</button>
      </div>
    </Modal>
  );
}

/* ---------------- Leads ---------------- */
function Leads() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => { setLoading(true); api.get("/admin/leads").then((r) => setList(r.data)).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const update = async (id, body) => { try { await api.patch(`/admin/leads/${id}`, body); toast.success("Updated"); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div data-testid="admin-leads">
      <div className="flex justify-end mb-4"><a href={exportUrl("leads")} className={`${btn} border-2 border-charcoal-900 text-charcoal-900 hover:bg-charcoal-900 hover:text-white px-4 py-2`}><Download size={15} /> Export CSV</a></div>
      <Card>
        {loading ? <Loading /> : list.length === 0 ? <Empty text="No commercial leads yet." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                <tr><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Facility</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3"><div className="font-medium">{l.name}</div><div className="text-xs text-gray-500">{l.company_name}</div>
                      <div className="text-xs"><a href={`tel:${l.phone}`} className="text-primary">{l.phone}</a> · <a href={`mailto:${l.email}`} className="text-primary">{l.email}</a></div></td>
                    <td className="px-4 py-3">{l.facility_type || "—"}<div className="text-xs text-gray-500">{l.frequency}</div></td>
                    <td className="px-4 py-3">
                      <select className={`${inp} py-1`} value={l.status} onChange={(e) => update(l.id, { status: e.target.value })} data-testid={`lead-status-${l.id}`}>{LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{l.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right"><button onClick={() => setOpen(l)} className="text-primary font-semibold hover:underline">View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {open && <LeadModal l={open} onClose={() => setOpen(null)} onSave={update} />}
    </div>
  );
}
function LeadModal({ l, onClose, onSave }) {
  const [notes, setNotes] = useState(l.notes || "");
  return (
    <Modal onClose={onClose}>
      <div className="flex justify-between items-center mb-4"><h2 className="text-lg font-bold text-charcoal-900">{l.name}</h2><button onClick={onClose}><X /></button></div>
      <table className="w-full text-sm mb-4">
        <tbody className="divide-y divide-gray-100">
          {[["Company", l.company_name], ["Phone", l.phone], ["Email", l.email], ["Property", l.property_address], ["Facility", l.facility_type], ["Sq Ft", l.square_footage], ["Frequency", l.frequency], ["Services", (l.services_needed || []).join(", ")], ["Preferred Contact", l.preferred_contact], ["Details", l.details]].map(([k, v]) => (
            <tr key={k}><td className="py-1.5 font-medium text-charcoal-600 w-40">{k}</td><td className="py-1.5">{v || "—"}</td></tr>
          ))}
        </tbody>
      </table>
      <label className="text-sm font-medium">Internal Notes</label>
      <textarea rows={3} className={`${inp} w-full`} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <button onClick={() => onSave(l.id, { notes })} className={`${btn} bg-primary text-white hover:bg-primary-hover px-4 py-2 mt-2`}>Save Notes</button>
    </Modal>
  );
}

/* ---------------- Contacts ---------------- */
function Contacts() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/contacts").then((r) => setList(r.data)).finally(() => setLoading(false)); }, []);
  return (
    <div data-testid="admin-contacts">
      <Card>
        {loading ? <Loading /> : list.length === 0 ? <Empty text="No messages yet." /> : (
          <div className="divide-y divide-gray-100">
            {list.map((c) => (
              <div key={c.id} className="p-5">
                <div className="flex justify-between flex-wrap gap-2">
                  <div className="font-medium text-charcoal-900">{c.name} <span className="text-xs bg-gray-100 rounded px-2 py-0.5 ml-1">{c.inquiry_type}</span></div>
                  <div className="text-xs text-gray-500">{c.created_at?.slice(0, 10)}</div>
                </div>
                <div className="text-sm text-charcoal-500"><a href={`mailto:${c.email}`} className="text-primary">{c.email}</a> · {c.phone}</div>
                <p className="text-sm text-charcoal-700 mt-2">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Settings ---------------- */
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Loading />;

  const save = async (patch) => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", patch); setS(data); toast.success("Saved"); }
    catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-settings">
      <Card className="p-6">
        <h3 className="font-semibold text-charcoal-900 mb-4">Availability & Booking</h3>
        <label className="text-sm font-medium block mb-2">Available Days</label>
        <div className="flex flex-wrap gap-2 mb-4">
          {DAY_LABELS.map((d, i) => (
            <button key={d} onClick={() => { const days = s.available_days.includes(i) ? s.available_days.filter((x) => x !== i) : [...s.available_days, i]; setS({ ...s, available_days: days }); }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border ${s.available_days.includes(i) ? "bg-primary text-white border-primary" : "border-gray-300 text-charcoal-700"}`} data-testid={`day-${i}`}>{d}</button>
          ))}
        </div>
        <label className="text-sm font-medium block mb-1">Time Windows (one per line)</label>
        <textarea rows={4} className={`${inp} w-full`} value={s.time_windows.join("\n")} onChange={(e) => setS({ ...s, time_windows: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} data-testid="time-windows" />
        <div className="grid sm:grid-cols-3 gap-4 mt-4">
          <div><label className="text-sm font-medium">Bookings / Day</label><input type="number" className={`${inp} w-full`} value={s.booking_limit_per_day} onChange={(e) => setS({ ...s, booking_limit_per_day: +e.target.value })} /></div>
          <div><label className="text-sm font-medium">Service Duration (min)</label><input type="number" className={`${inp} w-full`} value={s.service_duration_minutes} onChange={(e) => setS({ ...s, service_duration_minutes: +e.target.value })} /></div>
          <div><label className="text-sm font-medium">Cancellation Notice (hrs)</label><input type="number" className={`${inp} w-full`} value={s.cancellation_policy_hours} onChange={(e) => setS({ ...s, cancellation_policy_hours: +e.target.value })} /></div>
        </div>
        <label className="text-sm font-medium block mb-1 mt-4">Blocked Dates (one per line, YYYY-MM-DD)</label>
        <textarea rows={2} className={`${inp} w-full`} value={(s.blocked_dates || []).join("\n")} onChange={(e) => setS({ ...s, blocked_dates: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} data-testid="blocked-dates" />
        <button onClick={() => save({ available_days: s.available_days, time_windows: s.time_windows, booking_limit_per_day: s.booking_limit_per_day, service_duration_minutes: s.service_duration_minutes, cancellation_policy_hours: s.cancellation_policy_hours, blocked_dates: s.blocked_dates })} disabled={saving} className={`${btn} bg-primary text-white hover:bg-primary-hover px-5 py-2.5 mt-4`} data-testid="save-availability">Save Availability</button>
      </Card>

      <Card className="p-6">
        <h3 className="font-semibold text-charcoal-900 mb-4">Pricing</h3>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <div><label className="text-sm font-medium">Per Bedroom ($)</label><input type="number" className={`${inp} w-full`} value={s.price_per_bedroom} onChange={(e) => setS({ ...s, price_per_bedroom: +e.target.value })} /></div>
          <div><label className="text-sm font-medium">Per Bathroom ($)</label><input type="number" className={`${inp} w-full`} value={s.price_per_bathroom} onChange={(e) => setS({ ...s, price_per_bathroom: +e.target.value })} /></div>
          <div><label className="text-sm font-medium">Per Sq Ft ($)</label><input type="number" step="0.01" className={`${inp} w-full`} value={s.price_per_sqft} onChange={(e) => setS({ ...s, price_per_sqft: +e.target.value })} /></div>
        </div>
        <label className="text-sm font-medium block mb-2">Service Base Prices</label>
        <div className="space-y-2 mb-4">
          {s.services.map((svc, i) => (
            <div key={svc.id} className="flex items-center gap-3">
              <span className="text-sm flex-1 text-charcoal-700">{svc.name}</span>
              <input type="number" className={`${inp} w-28`} value={svc.base_price} onChange={(e) => { const ns = [...s.services]; ns[i] = { ...svc, base_price: +e.target.value }; setS({ ...s, services: ns }); }} data-testid={`price-${svc.id}`} />
            </div>
          ))}
        </div>
        <label className="text-sm font-medium block mb-2">Add-on Prices</label>
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {s.addons.map((a, i) => (
            <div key={a.id} className="flex items-center gap-3">
              <span className="text-sm flex-1 text-charcoal-700">{a.name}</span>
              <input type="number" className={`${inp} w-24`} value={a.price} onChange={(e) => { const na = [...s.addons]; na[i] = { ...a, price: +e.target.value }; setS({ ...s, addons: na }); }} data-testid={`addon-price-${a.id}`} />
            </div>
          ))}
        </div>
        <button onClick={() => save({ services: s.services, addons: s.addons, price_per_bedroom: s.price_per_bedroom, price_per_bathroom: s.price_per_bathroom, price_per_sqft: s.price_per_sqft })} disabled={saving} className={`${btn} bg-primary text-white hover:bg-primary-hover px-5 py-2.5`} data-testid="save-pricing">Save Pricing</button>
      </Card>

      <Card className="p-6">
        <h3 className="font-semibold text-charcoal-900 mb-4">Payment Settings</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><label className="text-sm font-medium">Payment Mode</label>
            <select className={`${inp} w-full`} value={s.payment_mode} onChange={(e) => setS({ ...s, payment_mode: e.target.value })} data-testid="payment-mode">
              <option value="full">Full payment at booking</option>
              <option value="deposit_fixed">Fixed deposit</option>
              <option value="deposit_percent">Percentage deposit</option>
              <option value="after_service">Payment after service</option>
            </select>
          </div>
          <div><label className="text-sm font-medium">Fixed Deposit ($)</label><input type="number" className={`${inp} w-full`} value={s.deposit_fixed_amount} onChange={(e) => setS({ ...s, deposit_fixed_amount: +e.target.value })} /></div>
          <div><label className="text-sm font-medium">Deposit Percent (%)</label><input type="number" className={`${inp} w-full`} value={s.deposit_percent} onChange={(e) => setS({ ...s, deposit_percent: +e.target.value })} /></div>
        </div>
        <button onClick={() => save({ payment_mode: s.payment_mode, deposit_fixed_amount: s.deposit_fixed_amount, deposit_percent: s.deposit_percent })} disabled={saving} className={`${btn} bg-primary text-white hover:bg-primary-hover px-5 py-2.5 mt-4`} data-testid="save-payment">Save Payment Settings</button>
      </Card>

      <Card className="p-6">
        <h3 className="font-semibold text-charcoal-900 mb-4">Notifications & Passwords</h3>
        <label className="text-sm font-medium">Notification Email</label>
        <input className={`${inp} w-full mb-3`} value={s.notification_email || ""} onChange={(e) => setS({ ...s, notification_email: e.target.value })} data-testid="notification-email" />
        <button onClick={() => save({ notification_email: s.notification_email })} disabled={saving} className={`${btn} bg-primary text-white hover:bg-primary-hover px-5 py-2.5 mb-6`} data-testid="save-notification">Save Email</button>
        <div className="border-t border-gray-100 pt-5 grid sm:grid-cols-2 gap-6">
          <ChangeAppPassword />
          <ChangeAdminPassword />
        </div>
      </Card>
    </div>
  );
}

function ChangeAppPassword() {
  const [pw, setPw] = useState("");
  const submit = async () => { if (pw.length < 4) return toast.error("Min 4 characters"); try { await api.post("/admin/application-password", { new_password: pw }); toast.success("Application password updated"); setPw(""); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div>
      <label className="text-sm font-medium block mb-1">Application Page Password</label>
      <input type="text" className={`${inp} w-full mb-2`} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="new-app-password" />
      <button onClick={submit} className={`${btn} border-2 border-charcoal-900 text-charcoal-900 hover:bg-charcoal-900 hover:text-white px-4 py-2`} data-testid="save-app-password">Update</button>
    </div>
  );
}
function ChangeAdminPassword() {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState("");
  const submit = async () => { if (nw.length < 8) return toast.error("Min 8 characters"); try { await api.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Admin password updated"); setCur(""); setNw(""); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div>
      <label className="text-sm font-medium block mb-1">Administrator Password</label>
      <input type="password" className={`${inp} w-full mb-2`} placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cur-admin-password" />
      <input type="password" className={`${inp} w-full mb-2`} placeholder="New password (min 8)" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="new-admin-password" />
      <button onClick={submit} className={`${btn} border-2 border-charcoal-900 text-charcoal-900 hover:bg-charcoal-900 hover:text-white px-4 py-2`} data-testid="save-admin-password">Update</button>
    </div>
  );
}

/* ---------------- Shared ---------------- */
function Modal({ children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto no-print-bg" onClick={onClose}>
      <div className={`bg-white rounded-md w-full ${wide ? "max-w-3xl" : "max-w-lg"} my-8 p-6`} onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}
function Loading() { return <div className="p-12 grid place-items-center"><Loader2 className="animate-spin text-primary" size={30} /></div>; }
function Empty({ text }) { return <div className="p-12 text-center text-gray-400 text-sm">{text}</div>; }
function StatusBadge({ status }) {
  const colors = {
    New: "bg-blue-100 text-blue-700", Reviewing: "bg-amber-100 text-amber-700", Interviewing: "bg-purple-100 text-purple-700",
    Hired: "bg-green-100 text-green-700", Declined: "bg-red-100 text-red-700",
    confirmed: "bg-green-100 text-green-700", completed: "bg-gray-200 text-gray-700", canceled: "bg-red-100 text-red-700",
    pending_payment: "bg-amber-100 text-amber-700",
  };
  return <span className={`text-xs font-semibold px-2 py-1 rounded ${colors[status] || "bg-gray-100 text-gray-600"}`}>{status}</span>;
}
